import React, { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { Markdown } from 'tiptap-markdown';
import './TiptapEditor.css';

// Extension: makes all heading nodes non-editable and prevents their modification
const ReadonlyHeadings = Extension.create({
  name: 'readonlyHeadings',

  addStorage() {
    return { enabled: true };
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin({
        key: new PluginKey('readonlyHeadings'),
        // Reject any transaction that adds, removes, or changes heading nodes
        filterTransaction: (transaction, state) => {
          if (!storage.enabled) return true;
          if (!transaction.docChanged) return true;

          const oldHeadings = [];
          state.doc.descendants((node) => {
            if (node.type.name === 'heading') {
              oldHeadings.push({ text: node.textContent, level: node.attrs.level });
            }
          });

          const newHeadings = [];
          transaction.doc.descendants((node) => {
            if (node.type.name === 'heading') {
              newHeadings.push({ text: node.textContent, level: node.attrs.level });
            }
          });

          if (oldHeadings.length !== newHeadings.length) return false;

          for (let i = 0; i < oldHeadings.length; i++) {
            if (oldHeadings[i].text !== newHeadings[i].text ||
                oldHeadings[i].level !== newHeadings[i].level) return false;
          }

          return true;
        },
        // Add contenteditable="false" decoration to every heading node
        props: {
          decorations(state) {
            const decorations = [];
            state.doc.descendants((node, pos) => {
              if (node.type.name === 'heading') {
                decorations.push(
                  Decoration.node(pos, pos + node.nodeSize, {
                    contenteditable: 'false',
                    class: 'readonly-heading',
                  })
                );
              }
            });
            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

const MenuBar = ({ editor, isFullscreen, onToggleFullscreen, readonlyHeadings = false }) => {
  // Force re-render when selection or content changes so active states update
  const [, setUpdateCount] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const handler = () => setUpdateCount(c => c + 1);
    editor.on('transaction', handler);
    return () => editor.off('transaction', handler);
  }, [editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Image URL');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="tiptap-toolbar">
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive('heading', { level: 1 }) ? 'active' : ''}
        title="Heading 1"
        disabled={readonlyHeadings}
      >
        H1
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={editor.isActive('heading', { level: 2 }) ? 'active' : ''}
        title="Heading 2"
        disabled={readonlyHeadings}
      >
        H2
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={editor.isActive('heading', { level: 3 }) ? 'active' : ''}
        title="Heading 3"
        disabled={readonlyHeadings}
      >
        H3
      </button>
      <div className="separator" />
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'active' : ''}
        title="Bold"
      >
        <strong>B</strong>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'active' : ''}
        title="Italic"
      >
        <em>I</em>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'active' : ''}
        title="Underline"
      >
        <u>U</u>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'active' : ''}
        title="Strikethrough"
      >
        <s>S</s>
      </button>
      <div className="separator" />
      <button
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={editor.isActive('blockquote') ? 'active' : ''}
        title="Blockquote"
      >
        &ldquo;
      </button>
      <button
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={editor.isActive('codeBlock') ? 'active' : ''}
        title="Code Block"
      >
        &lt;/&gt;
      </button>
      <button
        onClick={() => editor.chain().focus().toggleCode().run()}
        className={editor.isActive('code') ? 'active' : ''}
        title="Inline Code"
      >
        &lt;&gt;
      </button>
      <div className="separator" />
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'active' : ''}
        title="Bullet List"
      >
        &#8226;
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'active' : ''}
        title="Ordered List"
      >
        1.
      </button>
      <div className="separator" />
      <button onClick={addLink} className={editor.isActive('link') ? 'active' : ''} title="Link">
        &#128279;
      </button>
      <button onClick={addImage} title="Image">
        &#128247;
      </button>
      <button
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        className={editor.isActive('table') ? 'active' : ''}
        title="Insert Table"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="3" y1="15" x2="21" y2="15" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <line x1="15" y1="3" x2="15" y2="21" />
        </svg>
      </button>
      {editor.isActive('table') && (
        <>
          <button onClick={() => editor.chain().focus().addRowAfter().run()} title="Add Row After">
            +R
          </button>
          <button onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add Column After">
            +C
          </button>
          <button onClick={() => editor.chain().focus().deleteRow().run()} title="Delete Row">
            -R
          </button>
          <button onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete Column">
            -C
          </button>
          <button onClick={() => editor.chain().focus().deleteTable().run()} title="Delete Table" style={{ color: '#e53e3e' }}>
            &times;T
          </button>
        </>
      )}
      <div className="separator" />
      <button
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal Rule"
      >
        &#8212;
      </button>
      <button
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo"
      >
        &#8617;
      </button>
      <button
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo"
      >
        &#8618;
      </button>
      <div className="separator" />
      <button
        onClick={onToggleFullscreen}
        className={isFullscreen ? 'active' : ''}
        title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Fullscreen Editor'}
      >
        {isFullscreen ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4 14 10 14 10 20" />
            <polyline points="20 10 14 10 14 4" />
            <line x1="14" y1="10" x2="21" y2="3" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        )}
      </button>
    </div>
  );
};

const TiptapEditor = ({ value, onChange, style, placeholder, markdown = false, readonlyHeadings = false, readOnly = false }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Lock body scroll when expanded
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isFullscreen]);

  // Escape key to exit expanded mode
  useEffect(() => {
    if (!isFullscreen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isFullscreen]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          loading: 'lazy',
        },
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            width: { default: null, renderHTML: (attrs) => attrs.width ? { width: attrs.width } : {} },
            height: { default: null, renderHTML: (attrs) => attrs.height ? { height: attrs.height } : {} },
            style: { default: null, renderHTML: (attrs) => attrs.style ? { style: attrs.style } : {} },
          };
        },
      }),
      Underline,
      Table.configure({
        resizable: false,
        HTMLAttributes: { class: 'tiptap-table' },
      }),
      TableRow,
      TableCell,
      TableHeader,
      ...(markdown ? [Markdown.configure({
        html: true,
        transformCopiedText: true,
        transformPastedText: true,
      })] : []),
      ...(readonlyHeadings ? [ReadonlyHeadings] : []),
    ],
    editable: !readOnly,
    autofocus: false,
    content: value || '',
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(markdown ? editor.storage.markdown.getMarkdown() : editor.getHTML());
      }
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editable-area',
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
  });

  // Update editor content when value changes externally (e.g. switching notes)
  useEffect(() => {
    if (editor && value !== undefined && value !== null) {
      const currentContent = markdown
        ? editor.storage.markdown?.getMarkdown()
        : editor.getHTML();
      // Only update if the external value is substantially different
      // (avoid re-setting on every keystroke from our own onChange)
      if (currentContent !== value && !editor.isFocused) {
        // Temporarily disable readonly headings filter for programmatic updates
        if (editor.storage.readonlyHeadings) {
          editor.storage.readonlyHeadings.enabled = false;
        }
        editor.commands.setContent(value, false);
        if (editor.storage.readonlyHeadings) {
          editor.storage.readonlyHeadings.enabled = true;
        }
      }
    }
  }, [editor, value, markdown]);

  return (
    <>
      {isFullscreen && <div className="tiptap-fullscreen-overlay" onClick={toggleFullscreen} />}
      <div className={`tiptap-editor-container${isFullscreen ? ' tiptap-fullscreen' : ''}`} style={style}>
        {!readOnly && <MenuBar editor={editor} isFullscreen={isFullscreen} onToggleFullscreen={toggleFullscreen} readonlyHeadings={readonlyHeadings} />}
        <EditorContent editor={editor} />
      </div>
    </>
  );
};

export default TiptapEditor;
